import { FingerprintIcon, NetworkIcon, TagsIcon } from "lucide-react";

import { CopyButton } from "@/components/common/copy-button";
import { EnrichmentSummary } from "@/components/enrichment/enrichment-summary";
import { ShowViewHeader } from "@/components/refine-ui/views/show-view";
import {
  Chips,
  DetailField,
  DetailGrid,
  DetailSection,
  RawJsonCard,
  ShowHero,
  SystemSection,
  useShowRecord,
} from "@/components/show";
import { Badge } from "@/components/ui/badge";
import {
  ShowGenreViewType,
  showGenreViewSchema,
} from "@/pages/show-genres/schemas/show-genre.view";
import { EntityTinaTrace } from "@/pages/tina/components/entity-tina-trace";
import { ReEnrichButton } from "@/pages/tina/components/re-enrich-button";

export const ShowGenresShow = () => {
  const { record, raw } = useShowRecord<ShowGenreViewType>(
    showGenreViewSchema,
    (r) => `Genre | ${r.name ?? r.id}`,
  );

  return (
    <div className="p-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <ShowViewHeader />

        {record && (
          <>
            <ShowHero
              eyebrow="Show genre"
              title={record.name ?? "Untitled"}
              subtitle={record.slug ?? undefined}
              badges={(record.sections ?? []).map((s) => (
                <Badge key={s} variant="secondary">
                  {s}
                </Badge>
              ))}
              description={record.about}
            />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <DetailSection
                title="Taxonomy"
                icon={<NetworkIcon className="size-4" />}
              >
                <DetailGrid className="sm:grid-cols-1">
                  <DetailField label="Sections">
                    <Chips items={record.sections} />
                  </DetailField>
                  <DetailField label="Aliases">
                    <Chips items={record.aliases} variant="outline" />
                  </DetailField>
                  <DetailField label="Parents">
                    <Chips items={record.parents} variant="outline" />
                  </DetailField>
                </DetailGrid>
              </DetailSection>

              <DetailSection
                title="Identifiers"
                icon={<FingerprintIcon className="size-4" />}
              >
                <DetailGrid className="sm:grid-cols-1">
                  <DetailField label="ID" mono>
                    <span className="inline-flex items-center gap-1">
                      {record.id}
                      <CopyButton text={record.id} message="Copied genre ID" />
                    </span>
                  </DetailField>
                  <DetailField label="Slug" value={record.slug} mono />
                </DetailGrid>
              </DetailSection>
            </div>

            <EnrichmentSummary
              resource="show-genres"
              id={record.id}
              status={record.enrichmentStatus}
              enrichment={record.enrichment}
              bordered
            />

            {(record.transliterations || record.externalIds) && (
              <DetailSection
                title="External"
                icon={<TagsIcon className="size-4" />}
              >
                <pre className="overflow-auto rounded-md bg-muted p-4 font-mono text-xs leading-relaxed">
                  {JSON.stringify(
                    {
                      transliterations: record.transliterations,
                      externalIds: record.externalIds,
                    },
                    null,
                    2,
                  )}
                </pre>
              </DetailSection>
            )}

            {/* Tina — provenance, and the only non-SQL way to re-queue this
                entity's enrichment. Unlike
                artist/venue/geodata/event this entity has no per-entity enrich
                trigger anywhere in IMS, so `POST /enrichments` is not a second
                path to the same thing — it is the only one. */}
            <div className="flex flex-col gap-4">
              <div className="flex justify-end">
                <ReEnrichButton entityType="show_genre" entityId={record.id} />
              </div>
              <EntityTinaTrace
                entityType="show_genre"
                entityId={record.id}
                metadata={record.metadata}
                enrichment={record.enrichment}
              />
            </div>

            <SystemSection record={record} />

            <RawJsonCard data={raw} />
          </>
        )}
      </div>
    </div>
  );
};
